import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { CloseIcon } from "@/components/icons";
import { cn } from "../../utils/cn";

/**
 * Drawer — chrome primitive for content panels that slide in from the
 * edge of the screen. Mirrors the slot API of `Modal` (header / footer /
 * children) so consumers compose the same way regardless of whether the
 * surface is a centered modal or a drawer.
 *
 * Motion (DD-051):
 * - Desktop (≥ md): a floating panel inset from the right edge, with
 *   round corners, sliding in from the right.
 * - Mobile (< md): bottom sheet sliding in from the bottom. Edit forms
 *   read better above the thumb than a full-height panel on phones.
 * - A drawer that replaces another in place (`animateIn` false) appears
 *   without the slide and without the backdrop's fade.
 * - A drawer stacked on another (`overlay` false, finance-real 2026-10-22)
 *   adds a clear backdrop, for its scroll lock: the page darkens as with
 *   one drawer.
 *
 * The close button, padding, and Radix Dialog wiring are owned by this
 * primitive. Consumers only supply slot content — no consumer should be
 * reaching in to position a close button or manage padding.
 */
interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  /**
   * When true, the body wrapper applies `p-6` so its content shares the
   * same horizontal inset as the header (which is always `p-6`). Default
   * false to preserve flexibility for consumers that want full-bleed body
   * content (e.g. media viewers).
   */
  bodyPadding?: boolean;
  showCloseButton?: boolean;
  /**
   * The heading, which also names the dialog, as Modal's `title`. With a
   * custom `header` it only names the dialog, hidden.
   */
  title?: string;
  /**
   * Beside the visible title: a way to the item's own page, as an arrow (user comment, 2026-10-16). The title
   * stays the one heading and the dialog's name. Ignored with a custom `header`.
   */
  titleAside?: React.ReactNode;
  /** Hidden a11y title used by Radix when the visible title is custom. */
  a11yTitle?: string;
  /** Hidden a11y description used by Radix. */
  a11yDescription?: string;
  className?: string;
  /** Radix's open focus hook: preventDefault() to pick the focus target. */
  onOpenAutoFocus?: (event: Event) => void;
  /** Radix's close focus hook, run as it closes or unmounts: preventDefault() to pick where focus goes back. */
  onCloseAutoFocus?: (event: Event) => void;
  /** False for a drawer that replaces another in place: no slide, no backdrop fade. */
  animateIn?: boolean;
  /**
   * False for a drawer stacked on another one still open: the one under it
   * already darkens the page, so a second backdrop would darken it twice
   * (user comment, 2026-10-22). Outside it, a click still closes only it.
   */
  overlay?: boolean;
}

export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  header,
  footer,
  children,
  bodyPadding = false,
  showCloseButton = true,
  title,
  titleAside,
  a11yTitle = "Detail",
  a11yDescription = "Detail view.",
  className,
  onOpenAutoFocus,
  onCloseAutoFocus,
  animateIn = true,
  overlay = true,
}) => {
  // A title with no custom header is the visible heading: the dialog's name, shown once. Its aside sits beside it,
  // and a long title wraps beside it, as one with no aside does (finance-real 2026-10-22: the job drawer truncated while
  // the invoice drawer wrapped), instead of pushing the aside under the X. With no aside it wraps the same way.
  const titleShown = title !== undefined && !header;
  const titleHeading = titleAside ? (
    <div className="flex items-start gap-2 min-w-0">
      <Dialog.Title className="text-lg font-semibold min-w-0 [overflow-wrap:anywhere]">{title}</Dialog.Title>
      <span className="flex shrink-0">{titleAside}</span>
    </div>
  ) : (
    <Dialog.Title className="text-lg font-semibold min-w-0 [overflow-wrap:anywhere]">{title}</Dialog.Title>
  );
  const heading = header || (titleShown ? titleHeading : null);
  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        {overlay ? (
          <Dialog.Overlay
            className={cn(
              "fixed inset-0 z-50 bg-black/50 backdrop-blur-xs data-[state=closed]:animate-backdrop-exit",
              animateIn && "data-[state=open]:animate-backdrop-enter",
            )}
          />
        ) : (
          // Radix keeps a dialog's scroll lock on its overlay: with none, the drawer under it keeps the topmost lock, which
          // cancels every wheel and touch scroll in this one. A clear overlay takes the lock and darkens nothing more. It
          // follows the drawer under it in the DOM, so it sits above that drawer and below this one's panel.
          <Dialog.Overlay className="fixed inset-0 z-50 bg-transparent" />
        )}
        <Dialog.Content
          aria-describedby={undefined}
          // B6: index.css moves the toasts to the top left from md while a drawer's panel is on the page, off its X.
          data-drawer-panel=""
          onOpenAutoFocus={onOpenAutoFocus}
          onCloseAutoFocus={onCloseAutoFocus}
          // A toast sits above the overlay: dismissing it must not close the drawer.
          onInteractOutside={(event) => {
            if (event.target instanceof Element && event.target.closest("[data-toast-region]")) {
              event.preventDefault();
            }
          }}
          className={cn(
            "fixed z-50 bg-white shadow-xl flex flex-col overflow-hidden",
            // Mobile: bottom sheet
            "inset-x-0 bottom-0 h-[90dvh] rounded-t-2xl",
            "data-[state=closed]:animate-drawer-sheet-out",
            animateIn && "data-[state=open]:animate-drawer-sheet-in",
            // Desktop: a floating panel inset from the right edge
            "md:top-3 md:right-3 md:bottom-3 md:left-auto",
            "md:h-auto md:w-[calc(100%-1.5rem)] md:max-w-2xl md:rounded-2xl md:shadow-2xl",
            "md:data-[state=closed]:animate-drawer-panel-out",
            animateIn && "md:data-[state=open]:animate-drawer-panel-in",
            className,
          )}
        >
          {!titleShown && <Dialog.Title className="sr-only">{title ?? a11yTitle}</Dialog.Title>}
          <Dialog.Description className="sr-only">
            {a11yDescription}
          </Dialog.Description>

          {/* Header, with no rule under it (user comment, 2026-10-16). The
              close button is absolutely positioned so the header content owns
              the full padded width. `pr-10` reserves room so header content
              doesn't run under the X. */}
          {heading && (
            <div className="relative px-6 pt-6 pb-2 shrink-0">
              <div className="pr-10">{heading}</div>
              {showCloseButton && (
                <Dialog.Close
                  className="absolute top-5 right-5 p-1 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                  aria-label="Close"
                >
                  <CloseIcon className="w-5 h-5" />
                </Dialog.Close>
              )}
            </div>
          )}

          {/* Body. A menu inside never portals (DD-051), so it measures its room
              to this element's edges. With no footer the body is the sheet's
              bottom edge, so it clears the iPhone home indicator itself (0 on
              desktop). */}
          <div
            data-drawer-body=""
            className={cn(
              "flex-1 overflow-y-auto min-h-0",
              bodyPadding && "p-6",
              !footer && (bodyPadding ? "pb-[calc(env(safe-area-inset-bottom)_+_1.5rem)]" : "pb-[env(safe-area-inset-bottom)]"),
            )}
          >
            {children}
          </div>

          {/* Footer: the body's own surface, no band or rule above it (user
              comment, 2026-10-16), with the bottom raised by the safe area, so
              its buttons clear the iPhone home indicator (1.5rem elsewhere).
              The X is the way out: no footer carries a button that only
              closes (J12, J26). */}
          {footer && (
            <div className="flex items-center justify-end gap-3 px-6 pt-3 pb-[calc(env(safe-area-inset-bottom)_+_1.5rem)] shrink-0">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
